import ActionText from '../../core/ActionText';
import { STATUS_ACCEPTED, STATUS_PENDING, STATUS_REJECTED } from '../../core/utils/constants';
import Errored from '../../core/Errored';
import MenuSelector from '../../core/MenuSelector';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { UserConsumer } from '../../core/providers/UserProvider';
import { globalStyles as styles } from '../../core/style/gstheme';
import { makeCancelable } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import Suggestion from './Suggestion';

class SuggestionsViewer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            sortedSuggestions: null,
            pageCount: null,
            validStatus: [],
            visible: [],
            status: STATUS_PENDING,
            isLoadingSuggestions: true,
            isErrored: null
        };
        this.cancellable1 = null;
        this.cancellable2 = null;
    }

    componentDidMount() {
        this.getSuggestions();
    }

    componentWillUnmount() {
        if (this.cancellable1) this.cancellable1.cancel();
        if (this.cancellable2) this.cancellable2.cancel();
    }

    componentDidUpdate(prevProps) {
        const prevSuggestionCount = prevProps.data.isSuggestionSourceOf ? prevProps.data.isSuggestionSourceOf.length : 0;
        const suggestionCount = this.props.data.isSuggestionSourceOf ? this.props.data.isSuggestionSourceOf.length : 0;
        if (suggestionCount !== prevSuggestionCount) {
            this.getSuggestions();
        }
    }

    doGetSuggestions(suggestions) {
        this.cancellable1 = makeCancelable(GS.loadData(suggestions, {}, this.props.graph, false));
        return this.cancellable1;
    }

    getSuggestions = async () => {
        if (!this.props.data.isSuggestionSourceOf || this.props.data.isSuggestionSourceOf.length === 0) {
            this.setState({ isLoadingSuggestions: false });
            return;
        }
        this.setState(
            { isLoadingSuggestions: true },
            () => {
                this.doGetSuggestions(this.props.data.isSuggestionSourceOf)
                    .promise
                    .then(() => {
                        if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                        let pendingCount = 0;
                        let suggestionItems = {
                            altLabel: [],
                            prefLabel: [],
                            deprecation: [],
                            newConcept: [],
                            move: [],
                            remove: []
                        };
                        this.props.data.isSuggestionSourceOf.forEach(suggestion => {
                            const gsData = GS.data(suggestion, this.props.graph, false);
                            if (gsData && gsData.status && gsData.status[0] === STATUS_PENDING) {
                                pendingCount++;
                            }
                        });
                        const sortedSuggestions = this.props.data.isSuggestionSourceOf.sort((a, b) => {
                            const gsDataA = GS.data(a, this.props.graph, false);
                            const gsDataB = GS.data(b, this.props.graph, false);
                            const createdA = gsDataA && gsDataA.created ? gsDataA.created : 0;
                            const createdB = gsDataB && gsDataB.created ? gsDataB.created : 0;
                            return createdA < createdB ? 1 : -1;
                        });
                        let visible = [...sortedSuggestions];
                        let validStatus = ["All"];
                        sortedSuggestions.forEach(suggestion => {
                            const gsData = GS.data(suggestion, this.props.graph, false);
                            if (gsData && gsData.status && gsData.status[0] === STATUS_PENDING) {
                                if (gsData.type.includes("SynonymSuggestion")) {
                                    suggestionItems.altLabel.push(suggestion);
                                }
                                if (gsData.type.includes("LabelSuggestion")) {
                                    suggestionItems.prefLabel.push(suggestion);
                                }
                                if (gsData.type.includes("DeprecationSuggestion")) {
                                    suggestionItems.deprecation.push(suggestion);
                                }
                                if (gsData.type.includes("NewConceptSuggestion")) {
                                    suggestionItems.newConcept.push(suggestion);
                                }
                                if (gsData.type.includes("MovingSuggestion")) {
                                    suggestionItems.move.push(suggestion);
                                }
                                if (gsData.type.includes("RemovalSuggestion")) {
                                    suggestionItems.remove.push(suggestion);
                                }
                            }
                            if (gsData && gsData.status && gsData.status.length > 0) {
                                if (!validStatus.includes(gsData.status[0])) validStatus.push(gsData.status[0]);
                            }
                        });
                        if (this.state.status !== "All") {
                            visible = sortedSuggestions.filter(suggestion => {
                                const gsData = GS.data(suggestion, this.props.graph, false);
                                if (gsData && gsData.status.includes(this.state.status)) return true;
                                return false;
                            });
                        }
                        this.setState({
                            sortedSuggestions,
                            pendingCount,
                            validStatus,
                            isLoadingSuggestions: false,
                            visible
                        }, () => {
                            if (this.props.monitorSuggestionStatus) {
                                this.props.monitorSuggestionStatus(pendingCount, suggestionItems);
                            }
                        });
                    })
                    .catch((e) => {
                        if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
                    });
            });
    };

    monitorSuggestionUpdate = async (iri, property) => {
        if (property === "status") await this.getSuggestions();
    };

    handleStatusChange = (status) => this.setState({ status }, () => this.getSuggestions());

    render() {

        const { data, graph, defaultLang, entitlements, isReadOnly, optionForXL, useXL, iriOptions, useWorkflow, useSlashIRI,
            components, projectIRI, schemeIRI, id, isVisible, projectUsers, useCounterIRI, counters } = this.props;
        const isSchemeLevel = id === schemeIRI;
        const { sortedSuggestions, isErrored, visible, validStatus } = this.state;

        if (!isVisible) return null;
        if (isErrored) return <Errored data={isErrored} />;

        const editable = isSchemeLevel ?
            !data.deprecated || data.pendingDeletion
            :
            !(data.deprecated || data.pendingDeletion || (data.inScheme && !data.inScheme.includes(schemeIRI)));

        return (
            <UserConsumer>
                {({ user }) =>
                    <div>
                        <div style={{ width: 200, marginBottom: 12 }}>
                            <MenuSelector
                                dataTest="status-selector"
                                label="Status"
                                selected={STATUS_PENDING}
                                handleChange={this.handleStatusChange}
                                hintWord=" suggestion status"
                                options={[
                                    {
                                        "value": "All",
                                        "label": "All"
                                    },
                                    {
                                        "value": STATUS_PENDING,
                                        "label": "Open",
                                        "disabled": !validStatus.includes(STATUS_PENDING)
                                    },
                                    {
                                        "value": STATUS_ACCEPTED,
                                        "label": "Accepted",
                                        "disabled": !validStatus.includes(STATUS_ACCEPTED)
                                    },
                                    {
                                        "value": STATUS_REJECTED,
                                        "label": "Rejected",
                                        "disabled": !validStatus.includes(STATUS_REJECTED)
                                    }
                                ]} />

                        </div>
                        <div data-test="suggestions-list">
                            {visible.length === 0 && <ActionText offset="120px">There are no suggestions with the selected status.</ActionText>}
                            {sortedSuggestions && sortedSuggestions
                                .filter(suggestion => visible.includes(suggestion))
                                .map((suggestion) =>
                                    <div key={suggestion} style={{ marginBottom: 30 }}>
                                        <Suggestion
                                            schemeIRI={schemeIRI}
                                            projectIRI={projectIRI}
                                            components={components}
                                            useWorkflow={useWorkflow}
                                            useSlashIRI={useSlashIRI}
                                            defaultLang={defaultLang}
                                            handleStatusChange={this.handleStatusChange}
                                            key={suggestion}
                                            resourceStatus={data.status ? data.status[0] : null}
                                            id={suggestion}
                                            useCounterIRI={useCounterIRI}
                                            counters={counters}
                                            optionForXL={optionForXL}
                                            useXL={useXL}
                                            iriOptions={iriOptions}
                                            entitlements={entitlements}
                                            graph={graph}
                                            user={user.id}
                                            updateEvent={this.monitorSuggestionUpdate}
                                            isReadOnly={isReadOnly || !editable}
                                            users={projectUsers} />
                                    </div>
                                )}
                        </div>
                    </div>
                }
            </UserConsumer>
        );
    }
}

SuggestionsViewer.defaultProps = {
    isReadOnly: false,
    visible: false,
    optionForXL: false,
    useWorkflow: false,
    useXL: false
};

SuggestionsViewer.propTypes = {
    schemeIRI: PropTypes.string,
    projectIRI: PropTypes.string.isRequired,
    optionForXL: PropTypes.bool,
    useXL: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    components: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    isVisible: PropTypes.bool,
    useSlashIRI: PropTypes.bool,
    projectUsers: PropTypes.array.isRequired,
    monitorSuggestionStatus: PropTypes.func,
    entitlements: PropTypes.array.isRequired,
    useCounterIRI: PropTypes.bool,
    counter: PropTypes.object,
    graph: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    classes: PropTypes.object,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(withGS(SuggestionsViewer, ["ALL"], null, {}, true));